import React from "react";
import { Box, Button, Grid, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import moment from "moment";
import { CssTextField } from "../../components/TextField";
import { DATE, plural } from "../../services/moksha-reservations/format";
import {
    countOf,
    lineFieldId,
    lineTotal,
    NIGHT_FIELDS,
    nightFieldId,
    withNightRate,
    withRooms,
    withSameRate,
} from "./reservationDraft";
import { accentText, capacityLabel, focusRing, formatMoney } from "./presentation";
import { CONTROL_HEIGHT, useColors } from "./ui";

const NIGHT_INPUTS = [
    { kind: "rate", label: "Room rate", required: true },
    { kind: "extraBed", label: "Extra bed" },
    { kind: "meal", label: "Meal" },
];

function ReservationLine({ line, roomtype, nights, field, autoFocus, onChange, onRemove }) {
    const colors = useColors();
    const { mode } = useTheme().palette;
    const available = roomtype?.available_rooms ?? 0;
    const rooms = countOf(line.noOfRooms) || 0;
    const overCap = rooms > available && available > 0;

    const setField = (key) => (e) => onChange({ ...line, [key]: e.target.value });
    const setRooms = (e) => onChange(withRooms(line, e.target.value, available));
    const setSameRate = (kind) => (e) => onChange(withSameRate(line, kind, e.target.value, nights));
    const setRate = (kind, date) => (e) => onChange(withNightRate(line, kind, date, e.target.value));

    return (
        <Box
            component="section"
            aria-label={`${roomtype?.name || "Roomtype"} line`}
            width="100%"
            p={2}
            borderRadius={2}
            backgroundColor={colors.primary[500]}
        >
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h5" fontWeight={600} color={accentText(colors, mode)}>
                        {roomtype?.name || "Roomtype not free for these dates"}
                    </Typography>
                    <Typography variant="body2" color={colors.grey[300]}>
                        {plural(available, "room")} free for the whole stay
                        {capacityLabel(roomtype) && ` · ${capacityLabel(roomtype)}`}
                    </Typography>
                </Box>

                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item md={5} sm={6} xs={12}>
                        <CssTextField
                            fullWidth
                            select
                            required
                            size="small"
                            label="Rate plan"
                            value={line.mealPlanId}
                            onChange={setField("mealPlanId")}
                            autoFocus={autoFocus}
                            {...field(lineFieldId(line.key, "mealPlan"))}
                        >
                            {roomtype?.meal_plans?.map((mp) => (
                                <MenuItem key={mp.meal_plan_id} value={mp.meal_plan_id}>
                                    {mp.name?.toUpperCase()}
                                </MenuItem>
                            ))}
                        </CssTextField>
                    </Grid>
                    <Grid item md={2} sm={3} xs={6}>
                        <CssTextField
                            fullWidth
                            required
                            size="small"
                            type="number"
                            label="Rooms"
                            value={line.noOfRooms}
                            onChange={setRooms}
                            inputProps={{ min: 1, max: available }}
                            {...field(lineFieldId(line.key, "rooms"), `Up to ${available}`)}
                        />
                    </Grid>
                    {overCap && (
                        <Grid item md={5} sm={3} xs={6}>
                            <Button
                                variant="outlined"
                                onClick={() => onChange(withRooms(line, String(available), available))}
                                sx={{ height: CONTROL_HEIGHT, ...focusRing(colors) }}
                            >
                                Set to {plural(available, "room")}
                            </Button>
                        </Grid>
                    )}
                </Grid>

                <Box>
                    <Typography fontWeight={600}>Rates per room, per night (incl. GST)</Typography>
                    <Typography variant="body2" color={colors.grey[300]} mb={1.5}>
                        Type a value under "Same every night" to fill every night, then adjust any night on its own.
                        Extra bed and meal are optional.
                    </Typography>
                    <Grid container spacing={1} alignItems="flex-start">
                        <Grid item md={3} xs={12}>
                            <Typography variant="body2" fontWeight={600} pt={{ md: 1 }}>
                                Same every night
                            </Typography>
                        </Grid>
                        {NIGHT_INPUTS.map(({ kind, label }) => (
                            <Grid item key={kind} md={3} xs={4}>
                                <CssTextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={label}
                                    value={line[NIGHT_FIELDS[kind].same]}
                                    onChange={setSameRate(kind)}
                                    inputProps={{ min: 0, step: "any" }}
                                    helperText=" "
                                />
                            </Grid>
                        ))}
                        {nights.map((date) => (
                            <React.Fragment key={date}>
                                <Grid item md={3} xs={12}>
                                    <Typography variant="body2" pt={{ md: 1 }}>
                                        {moment(date, DATE).format("ddd, DD MMM")}
                                    </Typography>
                                </Grid>
                                {NIGHT_INPUTS.map(({ kind, label, required }) => (
                                    <Grid item key={kind} md={3} xs={4}>
                                        <CssTextField
                                            fullWidth
                                            required={required}
                                            size="small"
                                            type="number"
                                            label={label}
                                            placeholder={required ? undefined : "0"}
                                            value={line[NIGHT_FIELDS[kind].values]?.[date] ?? ""}
                                            onChange={setRate(kind, date)}
                                            inputProps={{
                                                min: 0,
                                                step: "any",
                                                "aria-label": `${label}, ${moment(date, DATE).format("ddd DD MMM")}`,
                                            }}
                                            {...field(nightFieldId(line.key, kind, date))}
                                        />
                                    </Grid>
                                ))}
                            </React.Fragment>
                        ))}
                    </Grid>
                </Box>

                <Stack
                    direction={{ xs: "column-reverse", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                >
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutline />}
                        onClick={onRemove}
                        aria-label={`Remove ${roomtype?.name || "this roomtype"}`}
                        sx={focusRing(colors)}
                    >
                        Remove
                    </Button>
                    <Typography color={colors.grey[300]}>
                        Subtotal ({plural(rooms, "room")} × {plural(nights.length, "night")}):{" "}
                        <Box component="b" color={colors.grey[100]}>
                            {formatMoney(lineTotal(line, nights))}
                        </Box>
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
}

export default ReservationLine;
